import { type BankStatementRow } from '@utazzo/shared';
import { UzDatePicker, UzInput, UzSelect } from '@utazzo/ui';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateDMY, formatInrPaise, isoToLocalDate, toDateInputIso } from '../invoices.helpers';
import { useBankStatement, useBankAccountOptions, useInvoiceAgents } from '../use-invoices';

import {
  ReportListScreen,
  type ReportColumn,
} from '@/components/report-list-screen/ReportListScreen';

/**
 * Bank Transaction Statement - report #19, a 1:1 port of
 * banktransactionstatement.aspx: a Dr/Cr passbook (customer receipts Dr,
 * supplier payments Cr) with Dr/Cr totals, built on the shared ReportListScreen.
 */

const rupees = (paise: string): number => Number(paise) / 100;
const drCr = (paise: string): string => (paise === '0' ? '' : formatInrPaise(paise));
const sumPaise = (rows: ReadonlyArray<BankStatementRow>, pick: (r: BankStatementRow) => string) =>
  formatInrPaise(rows.reduce((acc, r) => acc + BigInt(pick(r)), 0n).toString());

export const BankStatementPage = (): React.JSX.Element => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [agent, setAgent] = useState('');
  const [email, setEmail] = useState('');
  const [gstCustomerName, setGstCustomerName] = useState('');

  const query = {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(bankAccountId ? { bankAccountId } : {}),
    ...(agent ? { agent } : {}),
    ...(email.trim() ? { email: email.trim() } : {}),
    ...(gstCustomerName.trim() ? { gstCustomerName: gstCustomerName.trim() } : {}),
  };
  const reportQuery = useBankStatement(query);
  const rows = reportQuery.data?.rows ?? [];

  const banksQuery = useBankAccountOptions();
  const bankOptions = [
    { value: '', label: 'All banks' },
    ...(banksQuery.data ?? []).map((b) => ({ value: b.id, label: b.label || b.bankName })),
  ];
  const agentsQuery = useInvoiceAgents();
  const agentOptions = [
    { value: '', label: 'All agents' },
    ...(agentsQuery.data ?? []).map((a) => ({ value: a, label: a })),
  ];

  const columns: ReportColumn<BankStatementRow>[] = [
    {
      header: 'Proforma No.',
      value: (r) => r.proformaNumber ?? '',
      render: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="text-primary hover:underline">
          {r.proformaNumber ?? '-'}
        </Link>
      ),
    },
    { header: 'Customer Name', value: (r) => r.customerName ?? '' },
    { header: 'Email Id', value: (r) => r.email ?? '' },
    { header: 'GST Customer', value: (r) => r.gstCustomerName ?? '' },
    { header: 'Date', value: (r) => (r.date ? formatDateDMY(r.date) : '----') },
    {
      header: 'Amount (Dr.)',
      align: 'right',
      value: (r) => (r.amountDrPaise === '0' ? '' : rupees(r.amountDrPaise)),
      render: (r) => drCr(r.amountDrPaise),
      total: (rs) => sumPaise(rs, (r) => r.amountDrPaise),
    },
    {
      header: 'Amount (Cr.)',
      align: 'right',
      value: (r) => (r.amountCrPaise === '0' ? '' : rupees(r.amountCrPaise)),
      render: (r) => drCr(r.amountCrPaise),
      total: (rs) => sumPaise(rs, (r) => r.amountCrPaise),
    },
  ];

  return (
    <ReportListScreen
      title="Bank Transaction Statement"
      description="Dr/Cr passbook: customer receipts (Dr) and supplier payments (Cr) for a period."
      columns={columns}
      rows={rows}
      getRowKey={(r, i) => `${r.invoiceId}-${i}`}
      loading={reportQuery.isLoading}
      error={reportQuery.isError}
      emptyMessage="No transactions match these filters."
      exportName="bank-transaction-statement"
      filters={
        <>
          <UzDatePicker
            label="From"
            value={isoToLocalDate(from)}
            onValueChange={(d) => setFrom(d ? toDateInputIso(d) : '')}
            containerClassName="w-40"
          />
          <UzDatePicker
            label="To"
            value={isoToLocalDate(to)}
            onValueChange={(d) => setTo(d ? toDateInputIso(d) : '')}
            containerClassName="w-40"
          />
          <UzSelect
            label="Bank"
            value={bankAccountId}
            onValueChange={setBankAccountId}
            options={bankOptions}
            containerClassName="w-48"
          />
          <UzSelect
            label="Agent"
            value={agent}
            onValueChange={setAgent}
            options={agentOptions}
            containerClassName="w-44"
          />
          <UzInput
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            containerClassName="w-44"
          />
          <UzInput
            label="GST Customer"
            value={gstCustomerName}
            onChange={(e) => setGstCustomerName(e.target.value)}
            containerClassName="w-44"
          />
        </>
      }
    />
  );
};
